import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import PageLayout from "../components/PageLayout";
import { Link, useLocation, useNavigate } from "react-router-dom";
import TradenameAPI from "../api/tradename";
import { useSearchParams } from "react-router-dom";
import { FaStar } from "react-icons/fa6";
import SponsersAffiliationsSidebar from "../components/SponsersAffiliationsSidebar";
import TradenameListCell from "../components/tradenames/TradenameListCell";
import TradenameCard, { TradenameCardSkeleton } from "../components/tradenames/TradenameCard";
import TopBrandBadge from "../components/commons/TopBrandBadge";
import { RiMedicineBottleLine } from "react-icons/ri";
import { FaRegBuilding, FaChevronRight } from "react-icons/fa";
import FilterAndDisplay from "../components/FilterAndDisplay";

export default function TradeName() {
    const location = useLocation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [tradenames, setTradenames] = useState([]);
    const [tradenamesFeature, setTradenamesFeature] = useState([]);
    const [, setPagination] = useState({
        page: 1, totalItems: 0, totalPages: 0
    });
    const [currentPage, setCurrentPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');

    // Filter states
    const [selectedCategories, setSelectedCategories] = useState([]);
    const [selectedManufacturers, setSelectedManufacturers] = useState([]);
    const [selectedForms, setSelectedForms] = useState([]);
    const [displayFormat, setDisplayFormat] = useState('grid');

    // Dropdown states
    const [openCategoryDropdown, setOpenCategoryDropdown] = useState(false);
    const [openFormDropdown, setOpenFormDropdown] = useState(false);
    const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);

    const observer = useRef();
    const lastItemRef = useCallback(node => {
        if (isLoading) return;
        if (observer.current) observer.current.disconnect();
        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && hasMore) {
                setCurrentPage(prevPage => prevPage + 1);
            }
        });
        if (node) observer.current.observe(node);
    }, [isLoading, hasMore]);

    // Extract unique filter options from tradenames data
    const filterOptions = useMemo(() => {
        const categories = new Set();
        const manufacturers = new Set();
        const forms = new Set();
        tradenames.forEach(item => {
            if (item.categories) item.categories.forEach(c => categories.add(c));
            if (item.manufacturers) item.manufacturers.forEach(m => manufacturers.add(m.name));
            if (item.forms) item.forms.forEach(f => { if (f[0]) forms.add(f[0]); });
        });
        return {
            categories: Array.from(categories).sort(),
            manufacturers: Array.from(manufacturers).sort(),
            forms: Array.from(forms).sort()
        };
    }, [tradenames]);

    // Filter and sort tradenames
    const filteredAndSortedTradenames = useMemo(() => {
        return tradenames.filter(item => {
            if (selectedCategories.length > 0) {
                if (!selectedCategories.some(c => item.categories?.includes(c))) return false;
            }
            if (selectedManufacturers.length > 0) {
                if (!selectedManufacturers.some(m => item.manufacturers?.some(mfr => mfr.name === m))) return false;
            }
            if (selectedForms.length > 0) {
                if (!selectedForms.some(f => item.forms?.some(form => form[0] === f))) return false;
            }
            return true;
        });
    }, [tradenames, selectedCategories, selectedManufacturers, selectedForms]);

    const alphabetData = useMemo(() => {
        const alphabet = '#ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');
        const groups = {};
        alphabet.forEach(letter => {
            if (letter !== '#') {
                groups[letter] = filteredAndSortedTradenames.filter(item =>
                    item.name.toUpperCase().startsWith(letter)
                );
            } else {
                groups[letter] = filteredAndSortedTradenames.filter(item =>
                    !/^[A-Za-z]$/.test(item.name)
                );
            }
        });
        const validLetters = alphabet.filter(letter => groups[letter].length > 0);
        return { alphabet, groups, validLetters, totalItems: filteredAndSortedTradenames.length };
    }, [filteredAndSortedTradenames]);

    const [activeLetter, setActiveLetter] = useState(() => {
        const letterFromUrl = searchParams.get('letter');
        if (letterFromUrl && alphabetData.groups[letterFromUrl]?.length > 0) {
            return letterFromUrl;
        }
        return alphabetData.validLetters[0] || 'A';
    });

    const fetchTradenames = useCallback(async (letter, page) => {
        try {
            setIsLoading(true);
            setError(null);
            const data = await TradenameAPI.getByLetterPage(letter === '#' ? 'number' : letter, page);
            if (page === 1) {
                setTradenames(data.results || []);
            } else {
                setTradenames(prevItems => [...prevItems, ...(data.results || [])]);
            }
            setPagination(data.pagination || { page: 1, totalItems: 0, totalPages: 0 });
            setHasMore(data.pagination?.page < data.pagination?.totalPages);
        } catch (err) {
            setError('Failed to load trade names. Please try again later.');
            console.error('Error fetching tradenames:', err);
        } finally {
            setIsLoading(false);
        }
    }, []);

    const fetchTradenamesFeature = useCallback(async () => {
        try {
            const data = await TradenameAPI.getFeatures();
            setTradenamesFeature(data.results || []);
        } catch (err) {
            console.error('Error fetching featured tradenames:', err);
        }
    }, []);

    useEffect(() => {
        setTradenames([]);
        setCurrentPage(1);
        setHasMore(true);
        fetchTradenames(activeLetter, 1);
        fetchTradenamesFeature();
    }, [activeLetter, fetchTradenames, fetchTradenamesFeature]);

    useEffect(() => {
        if (currentPage > 1) fetchTradenames(activeLetter, currentPage);
    }, [currentPage, activeLetter, fetchTradenames]);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        params.set('letter', activeLetter);
        navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
    }, [activeLetter, navigate, location.pathname, location.search]);

    const handleSearch = async (e) => {
        e.preventDefault();
        try {
            setIsLoading(true);
            setError(null);
            const data = await TradenameAPI.search(searchQuery);
            setTradenames(data.results || []);
            setPagination(data.pagination || { page: 1, totalItems: 0, totalPages: 0 });
            setCurrentPage(1);
            setHasMore(false);
        } catch (err) {
            setError('Search failed. Please try again later.');
        } finally {
            setIsLoading(false);
        }
    };

    const switchToLetter = (letter) => {
        const params = new URLSearchParams(location.search);
        params.set('letter', letter);
        navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
        setActiveLetter(letter);
        setTradenames([]);
        setCurrentPage(1);
        setHasMore(true);
        fetchTradenames(letter, 1);
    };

    const handleCategoryToggle = (category) => {
        setSelectedCategories(prev => prev.includes(category) ? prev.filter(c => c !== category) : [...prev, category]);
    };
    const handleFormToggle = (form) => {
        setSelectedForms(prev => prev.includes(form) ? prev.filter(f => f !== form) : [...prev, form]);
    };
    const clearAllFilters = () => {
        setSelectedCategories([]);
        setSelectedManufacturers([]);
        setSelectedForms([]);
        setDisplayFormat('grid');
    };
    const hasActiveFilters = selectedCategories.length > 0 || selectedManufacturers.length > 0 || selectedForms.length > 0;

    if (error) {
        return (
            <PageLayout
                title="All Pharmaceutical"
                titleHighlight="Trade Names"
                subtitle="Browse our comprehensive list of pharmaceutical trade names"
                showSearch={true}
                pageTitle="Trade Names"
                initialType="tradenames"
                showGradient={true}
            >
                <div className="p-8 text-center border bg-white/60 border-white/40 rounded-2xl mt-6">
                    <div className="mb-4 text-16 text-red-600 font-medium">{error}</div>
                    <button
                        onClick={() => window.location.reload()}
                        className="px-5 py-2.5 text-14 font-medium text-white rounded-lg bg-primary hover:bg-primary/90 transition-colors min-h-[44px]"
                    >
                        Try Again
                    </button>
                </div>
            </PageLayout>
        );
    }

    return (
        <PageLayout
            title="All Pharmaceutical"
            titleHighlight="Trade Names"
            subtitle="Browse our comprehensive list of pharmaceutical trade names"
            showSearch={true}
            pageTitle="Trade Names"
            initialType="tradenames"
            showGradient={true}
            onSearch={handleSearch}
            searchValue={searchQuery}
            onSearchChange={(e) => setSearchQuery(e.target.value)}
        >
            {/* === TOP BRAND: Featured Section === */}
            {tradenamesFeature.length > 0 && (
                <section className="mt-6 mb-8" aria-label="Top brand trade names">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10">
                            <FaStar className="size-3.5 text-primary" />
                            <span className="text-14 font-medium text-primary">Top Brand</span>
                        </div>
                        <div className="flex-1 h-px bg-secondary/10" />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                        {tradenamesFeature.slice(0, 8).map((item, index) => (
                            <Link
                                key={`featured-${item.uri || index}`}
                                to={`/tradenames/${item.uri}`}
                                className="group"
                            >
                                <div className="flex items-center gap-3 p-4 h-full rounded-xl border-2 border-blue-100 bg-gradient-to-br from-blue-50/80 to-white transition-all duration-200 hover:border-primary/40 hover:shadow-md">
                                    <div className="flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary group-hover:bg-primary/20 transition-colors">
                                        <RiMedicineBottleLine className="size-5" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="font-medium text-15 text-secondary group-hover:text-primary transition-colors truncate">
                                            {item.name.replace("&amp;", "&")}
                                        </div>
                                        <div className="flex items-center gap-1.5 mt-1 text-13 text-secondary/60">
                                            <FaRegBuilding className="size-3" />
                                            <span className="truncate">{item.manufacturers?.[0]?.name || 'Manufacturer'}</span>
                                        </div>
                                        <TopBrandBadge className="mt-1.5" />
                                    </div>
                                    <FaChevronRight className="size-3 text-secondary/30 group-hover:text-primary transition-all group-hover:translate-x-0.5 flex-shrink-0" />
                                </div>
                            </Link>
                        ))}
                    </div>
                </section>
            )}

            {/* === ALPHABET NAVIGATION === */}
            <div className="sticky z-10 pt-3 pb-3 top-8 backdrop-blur-md bg-[#f5f5f7]/80">
                {/* Desktop Alpha Nav */}
                <div className="hidden md:block">
                    <div className="flex items-center gap-3">
                        <span className="text-14 font-medium text-secondary/60 flex-shrink-0">Browse A–Z</span>
                        <nav className="inline-flex flex-wrap gap-0.5 p-1 border rounded-xl bg-white/60 backdrop-blur-sm border-white/60" aria-label="Alphabetical navigation">
                            {alphabetData.alphabet.map(letter => {
                                const isActive = activeLetter === letter;
                                return (
                                    <button
                                        key={letter}
                                        onClick={() => switchToLetter(letter)}
                                        aria-current={isActive ? 'true' : undefined}
                                        className={`size-7 relative flex items-center justify-center rounded-lg text-14 font-text font-medium transition-all min-w-[28px] min-h-[28px] ${isActive
                                            ? "bg-primary text-white shadow-sm"
                                            : "text-secondary hover:bg-primary/5 hover:text-primary"
                                        }`}
                                    >
                                        {letter}
                                    </button>
                                );
                            })}
                        </nav>
                    </div>
                </div>

                {/* Mobile Alpha Nav + Filter button */}
                <div className="md:hidden flex items-center gap-2 overflow-hidden">
                    <FilterAndDisplay
                        mode="button"
                        filterOptions={filterOptions}
                        selectedCategories={selectedCategories}
                        selectedForms={selectedForms}
                        displayFormat={displayFormat}
                        openCategoryDropdown={openCategoryDropdown}
                        openFormDropdown={openFormDropdown}
                        isMobileFilterOpen={isMobileFilterOpen}
                        onCategoryToggle={handleCategoryToggle}
                        onFormToggle={handleFormToggle}
                        onCategoryDropdownToggle={() => setOpenCategoryDropdown(!openCategoryDropdown)}
                        onFormDropdownToggle={() => setOpenFormDropdown(!openFormDropdown)}
                        onDisplayFormatChange={setDisplayFormat}
                        onClearAllFilters={clearAllFilters}
                        onMobileFilterToggle={() => setIsMobileFilterOpen(!isMobileFilterOpen)}
                    />
                    <div className="flex overflow-x-auto py-1 gap-1.5 flex-1 min-w-0 pb-2">
                        {alphabetData.alphabet.map(letter => (
                            <button
                                key={letter}
                                onClick={() => switchToLetter(letter)}
                                className={`flex-shrink-0 h-9 w-9 flex items-center justify-center rounded-lg text-sm font-medium transition-colors ${activeLetter === letter
                                    ? "bg-primary text-white"
                                    : "bg-white/60 text-secondary hover:bg-primary/10 border border-white/70"
                                }`}
                            >
                                {letter}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* === MAIN CONTENT === */}
            <div className="mt-4 pb-12">
                {isLoading && currentPage === 1 ? (
                    <div className="flex items-center justify-center h-48">
                        <div className="w-10 h-10 border-2 border-blue-200 border-t-primary rounded-full animate-spin" />
                    </div>
                ) : (
                    <div className="flex gap-6">
                        {/* Sidebar - desktop only, with filters */}
                        <FilterAndDisplay
                            mode="sidebar"
                            filterOptions={filterOptions}
                            selectedCategories={selectedCategories}
                            selectedForms={selectedForms}
                            displayFormat={displayFormat}
                            openCategoryDropdown={openCategoryDropdown}
                            openFormDropdown={openFormDropdown}
                            isMobileFilterOpen={isMobileFilterOpen}
                            onCategoryToggle={handleCategoryToggle}
                            onFormToggle={handleFormToggle}
                            onCategoryDropdownToggle={() => setOpenCategoryDropdown(!openCategoryDropdown)}
                            onFormDropdownToggle={() => setOpenFormDropdown(!openFormDropdown)}
                            onDisplayFormatChange={setDisplayFormat}
                            onClearAllFilters={clearAllFilters}
                            onMobileFilterToggle={() => setIsMobileFilterOpen(!isMobileFilterOpen)}
                        >
                            <SponsersAffiliationsSidebar />
                        </FilterAndDisplay>

                        {/* Main content area */}
                        <div className="flex-1 min-w-0">
                            {alphabetData.groups[activeLetter]?.length > 0 ? (
                                <>
                                    {/* Result count + active filters summary */}
                                    <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                                        <p className="text-14 text-secondary/60">
                                            <span className="font-medium text-secondary">{alphabetData.groups[activeLetter].length}</span> trade names starting with "{activeLetter}"
                                            {hasActiveFilters && <span className="text-primary ml-1">(filtered)</span>}
                                        </p>
                                        {hasActiveFilters && (
                                            <button
                                                onClick={clearAllFilters}
                                                className="text-14 text-primary hover:text-primary/80 font-medium transition-colors"
                                            >
                                                Clear filters
                                            </button>
                                        )}
                                    </div>

                                    {/* Content grid or list */}
                                    {displayFormat === 'grid' ? (
                                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                            {alphabetData.groups[activeLetter].map((item, index) => {
                                                const isLastItem = index === alphabetData.groups[activeLetter].length - 1;
                                                return (
                                                    <React.Fragment key={`tradename-${item.uri || index}`}>
                                                        <TradenameCard
                                                            item={item}
                                                            isLastItem={isLastItem}
                                                            lastItemRef={isLastItem ? lastItemRef : null}
                                                        />
                                                        {index > 0 && index % 9 === 0 && (
                                                            <div className="col-span-1 sm:col-span-2 lg:col-span-3 block md:hidden py-2">
                                                                <SponsersAffiliationsSidebar showRange={index / 9 % 2 === 0 ? {start: 0, end: 3} : {start: 3, end: 6}} />
                                                            </div>
                                                        )}
                                                    </React.Fragment>
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        <div className="space-y-3">
                                            {alphabetData.groups[activeLetter].map((item, index) => {
                                                const isLastItem = index === alphabetData.groups[activeLetter].length - 1;
                                                return (
                                                    <TradenameListCell
                                                        key={`tradename-list-${item.uri || index}`}
                                                        item={item}
                                                        isLastItem={isLastItem}
                                                        lastItemRef={isLastItem ? lastItemRef : null}
                                                    />
                                                );
                                            })}
                                        </div>
                                    )}

                                    {/* Loading more */}
                                    {isLoading && currentPage > 1 && <TradenameCardSkeleton />}
                                </>
                            ) : (
                                <div className="p-10 text-center border bg-white/40 border-white/40 rounded-2xl">
                                    <div className="inline-flex items-center justify-center w-14 h-14 mb-4 text-22 font-bold rounded-full bg-primary/10 text-primary/70">
                                        {activeLetter}
                                    </div>
                                    <h3 className="text-16 font-medium text-secondary">No trade names found</h3>
                                    <p className="mt-2 text-14 text-secondary/60">
                                        {hasActiveFilters
                                            ? "Try adjusting your filters or selecting another letter"
                                            : "Try another letter from the navigation above"
                                        }
                                    </p>
                                    {hasActiveFilters && (
                                        <button
                                            onClick={clearAllFilters}
                                            className="mt-4 px-5 py-2.5 text-14 font-medium text-white rounded-lg bg-primary hover:bg-primary/90 transition-colors min-h-[44px]"
                                        >
                                            Clear All Filters
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Mobile Filter Drawer */}
            <FilterAndDisplay
                mode="drawer"
                filterOptions={filterOptions}
                selectedCategories={selectedCategories}
                selectedForms={selectedForms}
                displayFormat={displayFormat}
                openCategoryDropdown={openCategoryDropdown}
                openFormDropdown={openFormDropdown}
                isMobileFilterOpen={isMobileFilterOpen}
                onCategoryToggle={handleCategoryToggle}
                onFormToggle={handleFormToggle}
                onCategoryDropdownToggle={() => setOpenCategoryDropdown(!openCategoryDropdown)}
                onFormDropdownToggle={() => setOpenFormDropdown(!openFormDropdown)}
                onDisplayFormatChange={setDisplayFormat}
                onClearAllFilters={clearAllFilters}
                onMobileFilterToggle={() => setIsMobileFilterOpen(!isMobileFilterOpen)}
            />
        </PageLayout>
    );
}
